import React, { useState, useEffect } from 'react';
import { submitVolunteer, getSpotlightArchive } from '../lib/dataService';
import { toast } from 'sonner';
import { Send, User, ShieldCheck, ClipboardList, CalendarRange, ExternalLink, Award, HeartHandshake } from 'lucide-react';
import '../components/registration.css';

export const VOLUNTEER_AREAS = ['Table Official', 'Stats Station', 'Referee', 'Clock Operator', 'Coach'];

export const VOLUNTEER_LEAGUES = [
  { name: '10 & Under Leagues', schedule: 'Saturdays, 3:30 – 6:00 PM · October – December', youth: true },
  { name: '13 & Under Leagues', schedule: 'Saturdays, 1:00 – 4:00 PM · October – December', youth: true },
  { name: '15 & Under Leagues', schedule: 'Fridays, 6:00 – 9:00 PM · late September – December', youth: true },
  { name: 'Under 19 Leagues', schedule: 'Sundays, 3:00 – 7:30 PM & Mondays, 6:00 – 8:30 PM · September – December', youth: true },
  { name: 'Primary School League', schedule: 'Fridays, 1:00 – 3:00 PM · January – March', youth: true },
  { name: 'Under 14 School Leagues', schedule: 'Mondays & Wednesdays, 3:00 – 5:00 PM · January – March', youth: true },
  { name: 'Under 16 Leagues', schedule: 'Saturdays, 3:00 – 8:00 PM & Tuesdays, 6:00 – 8:30 PM · February – May', youth: true },
  { name: "National Men's League", schedule: 'Sundays, 3:00 – 8:30 PM & Mondays, 6:00 – 9:45 PM · January – May', youth: false },
  { name: "National Women's League", schedule: 'Sundays, 3:00 – 6:00 PM · February – April', youth: false },
  { name: '3x3 Tournaments', schedule: 'One-off events at various times of the year, generally 3 – 4 hours long', youth: false },
];

export default function VolunteerPage() {
  const [form, setForm] = useState({ name: '', dob: '', email: '', phone: '', experience: '' });
  const [areas, setAreas] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [docsAck, setDocsAck] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [wallOfFame, setWallOfFame] = useState([]);

  useEffect(() => {
    getSpotlightArchive().then(setWallOfFame).catch(() => setWallOfFame([]));
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const toggle = (list, setList) => (v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const youthSelected = leagues.some((l) => VOLUNTEER_LEAGUES.find((x) => x.name === l)?.youth);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Please enter your full name.'); return; }
    if (!form.dob.trim()) { toast.error('Please enter your date of birth.'); return; }
    if (!form.email.trim()) { toast.error('Please enter your email address.'); return; }
    if (!form.phone.trim()) { toast.error('Please enter your phone number.'); return; }
    if (areas.length === 0) { toast.error('Please select at least one area of interest.'); return; }
    if (leagues.length === 0) { toast.error('Please select at least one league.'); return; }
    if (youthSelected && !docsAck) { toast.error('Please acknowledge the required document submission.'); return; }
    setSending(true);
    try {
      await submitVolunteer({ ...form, areas, leagues, docs_ack: docsAck });
      setSubmitted(true);
      toast.success('Volunteer sign-up submitted!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit. Please try again.');
    }
    setSending(false);
  };

  return (
    <div className="static-page">
      <div className="page-hero">
        <div className="container">
          <span className="page-hero-label">Cayman Islands Basketball Association</span>
          <h1>Volunteer With CIBA</h1>
        </div>
      </div>
      <div className="container article-body-wrap section">
        {submitted ? (
          <div className="reg-form-wrap" style={{ borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
            <div className="reg-success" data-testid="volunteer-success">
              <ShieldCheck size={44} />
              <h3>Thank You for Volunteering!</h3>
              <p>We have received your sign-up, <strong>{form.name}</strong>. A confirmation email with the next steps has been sent to <strong>{form.email}</strong>.</p>
              <p>Please remember to email your <strong>police clearance certificate</strong> and <strong>child protection training certificate</strong> to{' '}
                <a href="mailto:childprotection@caymanbasketball.com">childprotection@caymanbasketball.com</a>.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="wp-content">
              <p>
                CIBA leagues run on the energy of our volunteers — table officials, stats keepers, referees, clock
                operators and coaches. No experience is necessary: <strong>training will be provided for the various
                volunteer roles.</strong> Sign up below and we will be in touch.
              </p>
            </div>

            {wallOfFame.length > 0 && (
              <div className="fame-wall" data-testid="wall-of-fame">
                <h2 className="fame-title"><Award size={22} /> Volunteer Wall of Fame</h2>
                <p className="fame-sub">Celebrating our Volunteers of the Month — thank you for making CIBA basketball happen!</p>
                <div className="fame-grid">
                  {wallOfFame.map((v) => (
                    <div key={v.id} className="fame-card" data-testid="fame-card">
                      {v.photo ? (
                        <img src={v.photo} alt={v.name} className="fame-photo" />
                      ) : (
                        <div className="fame-photo fame-photo-fallback"><HeartHandshake size={26} /></div>
                      )}
                      <div className="fame-info">
                        {v.month && <span className="fame-month">{v.month}</span>}
                        <strong className="fame-name">{v.name}</strong>
                        {v.role && <span className="fame-role">{v.role}</span>}
                        {v.quote && <p className="fame-quote">“{v.quote}”</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="reg-form-wrap" id="volunteer">
              <div className="reg-header">
                <h2>Volunteer Sign-Up</h2>
                <p>Fields marked <span className="reg-req">*</span> are required.</p>
              </div>

              <form onSubmit={handleSubmit}>
                <section className="reg-card">
                  <h3><User size={19} /> Your Information</h3>
                  <div className="reg-grid reg-grid-2">
                    <label>Full Name <span className="reg-req">*</span>
                      <input type="text" className="reg-input" value={form.name} onChange={set('name')} required data-testid="volunteer-name" />
                    </label>
                    <label>Date of Birth <span className="reg-req">*</span>
                      <input type="date" className="reg-input" value={form.dob} onChange={set('dob')} required data-testid="volunteer-dob" />
                    </label>
                    <label>Email Address <span className="reg-req">*</span>
                      <input type="email" className="reg-input" value={form.email} onChange={set('email')} required data-testid="volunteer-email" />
                    </label>
                    <label>Phone Number <span className="reg-req">*</span>
                      <input type="tel" className="reg-input" value={form.phone} onChange={set('phone')} required data-testid="volunteer-phone" />
                    </label>
                  </div>
                </section>

                <section className="reg-card">
                  <h3><ClipboardList size={19} /> Areas of Interest</h3>
                  <label className="reg-block-label">Which role(s) would you like to volunteer for? <span className="reg-req">*</span></label>
                  <div className="reg-terms">
                    {VOLUNTEER_AREAS.map((a) => (
                      <label key={a} className={`reg-term-option ${areas.includes(a) ? 'selected' : ''}`} data-testid={`volunteer-area-${a.toLowerCase().replace(/\s+/g, '-')}`}>
                        <input type="checkbox" checked={areas.includes(a)} onChange={() => toggle(areas, setAreas)(a)} />
                        <span>{a}</span>
                      </label>
                    ))}
                  </div>
                  <div className="reg-grid" style={{ marginTop: 18 }}>
                    <label>Previous experience in any of these areas
                      <textarea
                        className="reg-input"
                        rows={3}
                        value={form.experience}
                        onChange={set('experience')}
                        placeholder="Tell us about any previous experience — write 'None' if this is your first time. Training will be provided!"
                        data-testid="volunteer-experience"
                      />
                    </label>
                  </div>
                </section>

                <section className="reg-card">
                  <h3><CalendarRange size={19} /> Leagues</h3>
                  <label className="reg-block-label">Which league(s) would you like to volunteer for? <span className="reg-req">*</span></label>
                  <div className="volunteer-leagues">
                    {VOLUNTEER_LEAGUES.map((l) => (
                      <label key={l.name} className={`reg-term-option volunteer-league-option ${leagues.includes(l.name) ? 'selected' : ''}`} data-testid={`volunteer-league-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                        <input type="checkbox" checked={leagues.includes(l.name)} onChange={() => toggle(leagues, setLeagues)(l.name)} />
                        <span>
                          <strong>{l.name}</strong>
                          <em className="volunteer-league-schedule">{l.schedule}</em>
                        </span>
                      </label>
                    ))}
                  </div>
                </section>

                {youthSelected && (
                <section className="reg-card" data-testid="volunteer-youth-docs-card">
                  <h3><ShieldCheck size={19} /> Youth League Requirements</h3>
                  <div className="volunteer-docs-notice">
                    <p>You have selected one or more <strong>youth leagues</strong>. To volunteer with young people, you must email copies of <strong>both</strong> of the following documents to{' '}
                      <a href="mailto:childprotection@caymanbasketball.com">childprotection@caymanbasketball.com</a>:</p>
                    <ol>
                      <li>A <strong>police clearance certificate</strong> no more than 6 months old</li>
                      <li>Proof of <strong>child protection training</strong> no more than 1 year old — such as Darkness to Light training, which can also be taken online at{' '}
                        <a href="https://www.d2l.org/education/stewards-of-children/" target="_blank" rel="noreferrer">d2l.org/education/stewards-of-children <ExternalLink size={12} style={{ display: 'inline' }} /></a></li>
                    </ol>
                  </div>
                  <label className="reg-declaration" style={{ marginTop: 16 }}>
                    <input type="checkbox" checked={docsAck} onChange={(e) => setDocsAck(e.target.checked)} data-testid="volunteer-docs-ack" />
                    <span>
                      I understand that to volunteer with a youth league I must email a police clearance certificate
                      (no more than 6 months old) and proof of child protection training (no more than 1 year old) to
                      childprotection@caymanbasketball.com. <span className="reg-req">*</span>
                    </span>
                  </label>
                </section>
                )}

                <button type="submit" className="btn-primary reg-submit" disabled={sending} data-testid="volunteer-submit">
                  <Send size={16} /> {sending ? 'Submitting...' : 'Sign Up to Volunteer'}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
